const EMUToPx = (emu_value: number) => {
  return emu_value / 9525;
};

const EMUToTWIP = (emu_value: number) => {
  return emu_value / 635;
};

const TWIPToCm = (twip_value: number) => {
  return twip_value / 566.9291338583;
};

// const TWIP :number = 566.9291338583
// #region docxPageBuilder
export default async function docxPageBuilder(
  pageElements: Element[],
  document_xml_rel: any,
  media_files: any
): Promise<HTMLDivElement> {
  const new_page = document.createElement("div");

  const page = Promise.all(
    Array.from(pageElements).map(async (pageElement: Element, idx) => {
      if (pageElement.tagName === "w:p") {
        //contains text

        if (pageElement.getElementsByTagName("w:t").length > 0) {
          new_page.appendChild(await buildTextFragment(pageElement));
        } else if (pageElement.getElementsByTagName("pic:blipFill").length > 0) {
          //contains image
          new_page.appendChild(buildImageFragment(pageElement, document_xml_rel, media_files));
        } else if (pageElement.innerHTML === "") {
          //empty line

          const wrapper = document.createElement("div");

          wrapper.innerHTML = "<br>";
          new_page.appendChild(wrapper);
        }
      } else if (pageElement.tagName === "w:tbl") {
        //contains table

        new_page.appendChild(await buildTableFragment(pageElement));
      } else {
        /** IN case of unrecognizable element , leave blank space**/
        const wrapper = document.createElement("div");

        wrapper.innerHTML = "<br>";
        new_page.appendChild(wrapper);
      }
    })
  ).then(() => {
    return new_page;
  });

  return await page;
}
// #endregion

// #region buildTextFragment
async function buildTextFragment(pageElement: Element): Promise<HTMLDivElement> {
  const text_elements = pageElement.getElementsByTagName("w:t");
  const fragments: string[] = [];

  const wrapper = document.createElement("p");

  if (pageElement.getElementsByTagName("w:color").length > 0) {
    //that means it has color

    const color = pageElement.getElementsByTagName("w:color")[0].getAttribute("w:val");

    if (color !== null) {
      wrapper.style.setProperty("color", `#${color}`, "important");
    }
  }
  if (pageElement.getElementsByTagName("w:jc").length > 0) {
    //that means it has position

    const position = pageElement.getElementsByTagName("w:jc")[0].getAttribute("w:val");

    if (position !== null) {
      wrapper.style.setProperty("align-self", position, "important");
    }
  }
  if (pageElement.getElementsByTagName("w:sz").length > 0) {
    //that means it has font size

    const font_size = pageElement.getElementsByTagName("w:sz")[0].getAttribute("w:val");

    if (font_size !== null) {
      wrapper.style.setProperty("font-size", `${(parseFloat(font_size) / 2).toString()}pt`, "important");
    }
  }
  if (pageElement.getElementsByTagName("w:b").length > 0) {
    //that means it has bold font

    wrapper.style.setProperty("font-weight", "bold", "important");
  }

  for (let i = 0; i < text_elements.length; i++) {
    fragments.push(text_elements[i].textContent ?? "");
  }

  wrapper.innerHTML = fragments.join("");

  return wrapper;
}
// #endregion

// #region buildImageFragment
function buildImageFragment(pageElement: Element, document_xml_rel: any, media_files: any[]): HTMLImageElement {
  let img_name: string | null = "";
  const cx = pageElement.getElementsByTagName("pic:spPr")[0]?.getElementsByTagName("a:ext")[0]?.getAttribute("cx");
  const width = TWIPToCm(EMUToTWIP(parseFloat(cx ?? "1")));
  const cy = pageElement.getElementsByTagName("pic:spPr")[0]?.getElementsByTagName("a:ext")[0]?.getAttribute("cy");
  const height = TWIPToCm(EMUToTWIP(parseFloat(cy ?? "1")));
  const off_x = pageElement.getElementsByTagName("pic:spPr")[0]?.getElementsByTagName("a:off")[0]?.getAttribute("x");
  const margin_left = TWIPToCm(EMUToTWIP(parseFloat(off_x ?? "1")));

  const rid = pageElement
    .getElementsByTagName("pic:blipFill")[0]
    .getElementsByTagName("a:blip")[0]
    ?.getAttribute("r:embed");

  //2.get the real image name
  const rel_tags: HTMLCollectionOf<Element> = document_xml_rel.getElementsByTagName("Relationship");
  const wrapper = document.createElement("img");
  wrapper.setAttribute("style", "max-width : 100% ; max-height : 100% ");
  Array.from(rel_tags).forEach((tag, idx) => {
    if (tag.getAttribute("Id") === rid) {
      img_name = tag.getAttribute("Target");

      //3.build the image

      const image = media_files?.find((file) => {
        return file.name === img_name;
      });

      wrapper.src = URL.createObjectURL(image.content);
      wrapper.setAttribute("style", `margin-left : ${margin_left}cm ; width : ${width}cm ; height : ${height}cm`);
    }
  });

  return wrapper;
}
// #endregion

// #region buildTableFragment
async function buildTableFragment(pageElement: Element): Promise<HTMLTableElement> {
  const rows = pageElement.getElementsByTagName("w:tr");
  const table_el = document.createElement("table");
  table_el.setAttribute("style", "border-collapse: collapse");

  const table = Promise.all(
    Array.from(rows).map((row, row_idx) => {
      const row_el = document.createElement("tr");
      const row_cells = row.getElementsByTagName("w:tc");

      Array.from(row_cells).forEach((cell, cell_idx) => {
        const cell_width = cell.getElementsByTagName("w:tcW")[0]?.getAttribute("w:w");
        const width = TWIPToCm(parseFloat(cell_width ?? "1"));
        const cell_el = document.createElement("td");
        cell_el.setAttribute("style", `border : 0.5px lightgray solid ; width : ${width}cm`);

        if (cell.getElementsByTagName("w:t").length === 0) {
          // cell_el.innerHTML = `&nbsp;`
          row_el.appendChild(cell_el);
        } else {
          // Get all the paragraphs within the cell
          const paragraphs = cell.getElementsByTagName("w:p");
          Array.from(paragraphs).forEach((paragraph) => {
            const paragraphElement = document.createElement("p");
            applyParagraphProperties(paragraphElement, paragraph.getElementsByTagName("w:pPr")[0]);

            // Handle the text content and apply character properties for each <w:t> element
            const text_elements = paragraph.getElementsByTagName("w:t");
            Array.from(text_elements).forEach((textElement) => {
              const textContent = textElement.textContent;
              if (textContent) {
                const span = document.createElement("span");
                span.textContent = textContent;

                // Check for character properties (<w:rPr>) within the text element
                const characterProperties = textElement.getElementsByTagName("w:rPr")[0];
                applyCharacterProperties(span, characterProperties);

                paragraphElement.appendChild(span);
              }
            });

            cell_el.appendChild(paragraphElement);
          });

          row_el.appendChild(cell_el);
        }

        if (cell_idx === row_cells.length - 1) {
          table_el.appendChild(row_el);
        }
      });
    })
  ).then(() => {
    return table_el;
  });

  return await table;
}
// #endregion

// #region applyParagraphProperties
function applyParagraphProperties(element: HTMLElement, paragraphProperties: Element | undefined) {
  if (paragraphProperties) {
    const color = paragraphProperties.getElementsByTagName("w:color")[0]?.getAttribute("w:val");
    const font = paragraphProperties.getElementsByTagName("w:rFonts")[0]?.getAttribute("w:ascii");

    const bold = paragraphProperties.getElementsByTagName("w:b")[0];
    const italic = paragraphProperties.getElementsByTagName("w:i")[0];

    if (color !== null) {
      element.style.color = `#${color}`;
    }

    if (font !== null) {
      element.style.fontFamily = font;
    }

    if (bold && bold.getAttribute("w:val") === "true") {
      element.style.fontWeight = "bold";
    } else {
      element.style.fontWeight = "normal";
    }

    if (italic && italic.getAttribute("w:val") === "true") {
      element.style.fontStyle = "italic";
    } else {
      element.style.fontStyle = "normal";
    }

    // Check for alignment property
    const alignment = paragraphProperties.getElementsByTagName("w:jc")[0]?.getAttribute("w:val");
    if (alignment) {
      element.style.textAlign = alignment;
    }

    // Check for spacing properties
    const spacing = paragraphProperties.getElementsByTagName("w:spacing")[0];

    if (spacing) {
      const lineSpacing = spacing.getAttribute("w:line");
      if (lineSpacing) {
        element.style.lineHeight = `${parseInt(lineSpacing) / 20}pt`;
      }
      const beforeSpacing = spacing.getAttribute("w:before");
      if (beforeSpacing) {
        element.style.marginTop = `${EMUToPx(parseInt(beforeSpacing)) + 10}px`;
      }
      const afterSpacing = spacing.getAttribute("w:after");
      if (afterSpacing) {
        element.style.marginBottom = `${EMUToPx(parseInt(afterSpacing)) + 10}px`;
      }
    }
  }
}
// #endregion

// #region applyCharacterProperties
function applyCharacterProperties(element: HTMLElement, characterProperties: Element | undefined) {
  if (characterProperties) {
    // Check for bold property
    const isBold = characterProperties.getElementsByTagName("w:b")[0];
    if (isBold) {
      element.style.fontWeight = "bold";
    }
    // Check for italic property
    const isItalic = characterProperties.getElementsByTagName("w:i")[0];
    if (isItalic) {
      element.style.fontStyle = "italic";
    }
    // Check for font color property
    const fontColor = characterProperties.getElementsByTagName("w:color")[0]?.getAttribute("w:val");
    if (fontColor) {
      element.style.color = `#${fontColor}`;
    }

    // Check for text transform property
    const textTransform = characterProperties.getElementsByTagName("w:smallCaps")[0];
    if (textTransform) {
      element.style.textTransform = "uppercase";
    }

    // Add more styling properties as needed
  }
}
// #enregion
